import { Fragment, useEffect, useState } from "react";

import { useGetChatHistoryBySession } from "@/api/chat/get-chat-history-by-session";
import { Card } from "@/components/ui/card";
import ChatBubble from "@/components/ui/chat-bubble";
import { useSearchParams } from "react-router";
import { Skeleton } from "@/components/ui/skeleton";
import NoDataFound from "@/components/layout/no-data-found";
import { Send } from "lucide-react";

interface ChatHistory {
    id?: string;
    prompt: string;
    response: string;
    status: number;
}

interface MessageType {
    id?: string;
    message: string;
    messageFrom: string;
    status: number;
}

interface ChatHistoryProps {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    sendMessage?: any;
    messages?: any;
}

const ConversationHistory: React.FC<ChatHistoryProps> = ({ sendMessage, messages }) => {
    const [message, setMessage] = useState("");
    const [messageList, setMessageList] = useState<MessageType[]>([]);

    const [searchParams] = useSearchParams();
    const { data: chatHistory, isLoading } = useGetChatHistoryBySession({
        sessionId: searchParams.get("chat_id"),
    });

    const handleSendMessage = () => {
        if (!message) return;
        sendMessage(message);
        setMessageList([
            ...messageList,
            {
                message: message,
                messageFrom: "BOT",
                status: 0,
            },
        ]);
        setMessage("");
    };

    useEffect(() => {
        const tempMessageList = [...messageList];

        if (messages?.length > 0 && messages[messages.length - 1].includes(`Message from customer ${searchParams.get("chat_id")}:`)) {
            const message = messages[messages.length - 1].replace(`Message from customer ${searchParams.get("chat_id")}:`, "");
            const messageToInsert = {
                messageFrom: "USER",
                message: message,
                status: 0,
            };
            tempMessageList.push(messageToInsert);

            setMessageList(tempMessageList);
        }
    }, [messages]);

    return (
        <Card className="relative border pb-20">
            <div className="h-[calc(100vh-530px)] overflow-y-auto scroll-smooth md:h-[calc(100vh-165px)]">
                <div className="z-10 sticky top-0 flex items-center justify-between rounded-t-xl border-b bg-white p-4">
                    <h6 className="font-semibold">Conversation History</h6>
                </div>
                {!searchParams.get("chat_id") && <NoDataFound message="No Chat History Available" />}
                <div className="w-full p-4">
                    <ul className="space-y-3">
                        {isLoading &&
                            [1, 2, 3].map((item) => (
                                <Fragment key={item}>
                                    <li className="flex gap-2">
                                        <Skeleton className="size-10 rounded-full" />
                                        <Skeleton className="h-20 rounded-xl w-lg rounded-bl-none" />
                                    </li>
                                    <li className="flex justify-end gap-2">
                                        <Skeleton className="h-20 rounded-xl w-lg rounded-br-none" />
                                        <Skeleton className="size-10 rounded-full" />
                                    </li>
                                </Fragment>
                            ))}
                        {chatHistory?.data?.map((chat: ChatHistory) => (
                            <Fragment key={chat?.id}>
                                {chat?.prompt && (
                                    <li>
                                        <ChatBubble orientation="left">{chat?.prompt}</ChatBubble>
                                    </li>
                                )}
                                {chat?.response && (
                                    <li>
                                        <ChatBubble messageFrom="bot" reaction={chat?.status}>
                                            {chat?.response}
                                        </ChatBubble>
                                    </li>
                                )}
                            </Fragment>
                        ))}
                        {messageList?.map((chat: MessageType, index: number) => (
                            <Fragment key={index}>
                                <li>
                                    <ChatBubble
                                        messageFrom={chat.messageFrom === "BOT" ? "bot" : "user"}
                                        orientation={chat.messageFrom === "BOT" ? "right" : "left"}
                                        reaction={chat?.status}
                                    >
                                        {chat?.message}
                                    </ChatBubble>
                                </li>
                                {/*{chat?.prompt &&*/}
                                {/*    <li>*/}
                                {/*        <ChatBubble orientation="left">{chat?.prompt}</ChatBubble>*/}
                                {/*    </li>*/}
                                {/*}*/}
                            </Fragment>
                        ))}
                    </ul>
                </div>
            </div>
            {searchParams?.get("type") === "agent-chat" && (
                <div className="absolute bottom-0 w-full rounded-b-xl border-t bg-white p-4">
                    <input
                        type="text"
                        placeholder="Type a message..."
                        className="h-12 w-full rounded-xl border border-none bg-blue-100 px-4 pr-14 outline-none"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                handleSendMessage();
                            }
                        }}
                    />
                    <button
                        className="absolute right-6 top-1/2 -translate-y-1/2 rounded-full bg-blue-500 p-2 font-semibold text-white"
                        onClick={handleSendMessage}
                    >
                        <Send className="size-4" />
                    </button>
                </div>
            )}
        </Card>
    );
};

export default ConversationHistory;
